"use client";

import type {
  ImagineTools,
  ImagineUIToolParts,
} from "@imagine/ai-service/shared-types";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";

// Extract basename from path (e.g., "src/routes/index.tsx" -> "index.tsx")
function getBasename(path: string | undefined): string {
  if (!path) return "";
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

// File component with tooltip showing full path
function FilePathSpan({ path }: { path: string | undefined }) {
  if (!path) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground cursor-help">
          {getBasename(path)}
        </span>
      </TooltipTrigger>
      <TooltipContent className="px-2 py-1" sideOffset={4}>
        <p className="font-mono text-[10px]">{path}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function getToolCallDisplayByState(part: ImagineUIToolParts) {
  switch (part.type) {
    case "tool-edit_file":
      return {
        loading: ({ input }: { input: ImagineTools["edit_file"]["input"] }) =>
          input?.target_filepath && (
            <>
              Editing file <FilePathSpan path={input?.target_filepath} />
              ...
            </>
          ),
        success: ({
          input,
        }: {
          input: ImagineTools["edit_file"]["input"];
          output: ImagineTools["edit_file"]["output"];
        }) =>
          input?.target_filepath && (
            <>
              Edited file <FilePathSpan path={input?.target_filepath} />
            </>
          ),
        error: ({
          input,
        }: {
          input: ImagineTools["edit_file"]["input"];
          output: ImagineTools["edit_file"]["output"];
        }) => (
          <>
            Error editing file <FilePathSpan path={input?.target_filepath} />
          </>
        ),
      };

    case "tool-read_file":
      return {
        loading: ({ input }: { input: ImagineTools["read_file"]["input"] }) =>
          input?.path && (
            <>
              Reading file <FilePathSpan path={input?.path} />
              ...
            </>
          ),
        success: ({
          input,
        }: {
          input: ImagineTools["read_file"]["input"];
          output: ImagineTools["read_file"]["output"];
        }) =>
          input?.path && (
            <>
              Read file <FilePathSpan path={input?.path} />
            </>
          ),
        error: ({
          input,
        }: {
          input: ImagineTools["read_file"]["input"];
          output: ImagineTools["read_file"]["output"];
        }) => (
          <>
            Error reading file <FilePathSpan path={input?.path} />
          </>
        ),
      };

    case "tool-write_file":
      return {
        loading: ({ input }: { input: ImagineTools["write_file"]["input"] }) =>
          input?.path && (
            <>
              Writing file <FilePathSpan path={input?.path} />
              ...
            </>
          ),
        success: ({
          input,
        }: {
          input: ImagineTools["write_file"]["input"];
          output?: ImagineTools["write_file"]["output"];
        }) =>
          input?.path && (
            <>
              Wrote file <FilePathSpan path={input?.path} />
            </>
          ),
        error: ({
          input,
        }: {
          input: ImagineTools["write_file"]["input"];
          output?: ImagineTools["write_file"]["output"];
        }) => (
          <>
            Error writing file <FilePathSpan path={input?.path} />
          </>
        ),
      };

    case "tool-list_files_in_directory":
      return {
        loading: ({
          input,
        }: {
          input: ImagineTools["list_files_in_directory"]["input"];
        }) =>
          input?.path && (
            <>
              Listing files in directory <FilePathSpan path={input?.path} />
              ...
            </>
          ),
        success: ({
          input,
        }: {
          input: ImagineTools["list_files_in_directory"]["input"];
          output?: ImagineTools["list_files_in_directory"]["output"];
        }) =>
          input?.path && (
            <>
              Listed files in directory <FilePathSpan path={input?.path} />
            </>
          ),
        error: ({
          input,
        }: {
          input: ImagineTools["list_files_in_directory"]["input"];
          output?: ImagineTools["list_files_in_directory"]["output"];
        }) => (
          <>
            Error listing files in directory <FilePathSpan path={input?.path} />
          </>
        ),
      };

    case "tool-delete_file":
      return {
        loading: ({ input }: { input: ImagineTools["delete_file"]["input"] }) =>
          input?.path && (
            <>
              Deleting file <FilePathSpan path={input?.path} />
              ...
            </>
          ),
        success: ({
          input,
        }: {
          input: ImagineTools["delete_file"]["input"];
          output?: ImagineTools["delete_file"]["output"];
        }) =>
          input?.path && (
            <>
              Deleted file <FilePathSpan path={input?.path} />
            </>
          ),
        error: ({
          input,
        }: {
          input: ImagineTools["delete_file"]["input"];
          output?: ImagineTools["delete_file"]["output"];
        }) => (
          <>
            Error deleting file <FilePathSpan path={input?.path} />
          </>
        ),
      };

    case "tool-move_file":
      return {
        loading: ({ input }: { input: ImagineTools["move_file"]["input"] }) =>
          input?.path && (
            <>
              Moving file <FilePathSpan path={input?.path} />
              ...
            </>
          ),
        success: ({
          input,
        }: {
          input: ImagineTools["move_file"]["input"];
          output?: ImagineTools["move_file"]["output"];
        }) =>
          input?.path && (
            <>
              Moved file <FilePathSpan path={input?.path} />
            </>
          ),
        error: ({
          input,
        }: {
          input: ImagineTools["move_file"]["input"];
          output?: ImagineTools["move_file"]["output"];
        }) => (
          <>
            Error moving file <FilePathSpan path={input?.path} />
          </>
        ),
      };

    case "tool-get_server_logs":
      return {
        loading: ({
          input,
        }: {
          input: ImagineTools["get_server_logs"]["input"];
        }) => input?.lines && <>Checking server logs...</>,
        success: ({
          input,
        }: {
          input: ImagineTools["get_server_logs"]["input"];
          output?: ImagineTools["get_server_logs"]["output"];
        }) => `Checked server logs`,
        error: ({
          input,
        }: {
          input: ImagineTools["get_server_logs"]["input"];
          output?: ImagineTools["get_server_logs"]["output"];
        }) => `Error checking server logs`,
      };

    case "tool-install_npm_packages":
      return {
        loading: ({
          input,
        }: {
          input: ImagineTools["install_npm_packages"]["input"];
        }) =>
          `Installing ${input?.packages?.length > 1 ? "packages" : "package"} ${formatPackagesList(input?.packages)}...`,
        success: ({
          input,
        }: {
          input: ImagineTools["install_npm_packages"]["input"];
          output?: ImagineTools["install_npm_packages"]["output"];
        }) =>
          `Installed ${input?.packages?.length > 1 ? "packages" : "package"} ${formatPackagesList(input?.packages)}`,
        error: ({
          input,
        }: {
          input: ImagineTools["install_npm_packages"]["input"];
          output?: ImagineTools["install_npm_packages"]["output"];
        }) =>
          `Error installing ${input?.packages?.length > 1 ? "packages" : "package"} ${formatPackagesList(input?.packages)}`,
      };

    case "tool-uninstall_npm_packages":
      return {
        loading: ({
          input,
        }: {
          input: ImagineTools["uninstall_npm_packages"]["input"];
        }) =>
          `Uninstalling ${input?.packages?.length > 1 ? "packages" : "package"} ${formatPackagesList(input?.packages)}...`,
        success: ({
          input,
        }: {
          input: ImagineTools["uninstall_npm_packages"]["input"];
          output?: ImagineTools["uninstall_npm_packages"]["output"];
        }) =>
          `Uninstalled ${input?.packages?.length > 1 ? "packages" : "package"} ${formatPackagesList(input?.packages)}`,
        error: ({
          input,
        }: {
          input: ImagineTools["uninstall_npm_packages"]["input"];
          output?: ImagineTools["uninstall_npm_packages"]["output"];
        }) =>
          `Error uninstalling ${input?.packages?.length > 1 ? "packages" : "package"} ${formatPackagesList(input?.packages)}`,
      };

    case "tool-grep":
      return {
        loading: ({ input }: { input: ImagineTools["grep"]["input"] }) =>
          input?.pattern && `Grepping ${input?.pattern}...`,
        success: ({
          input,
        }: {
          input: ImagineTools["grep"]["input"];
          output?: ImagineTools["grep"]["output"];
        }) =>
          input?.pattern &&
          `Grepped "${input?.pattern}"${input?.path ? ` in ${input?.path}` : ""}...`,
        error: ({
          input,
        }: {
          input: ImagineTools["grep"]["input"];
          output?: ImagineTools["grep"]["output"];
        }) =>
          input?.pattern &&
          `Error grepping "${input?.pattern}"${input?.path ? ` in ${input?.path}` : ""}`,
      };

    case "tool-glob_files":
      return {
        loading: ({ input }: { input: ImagineTools["glob_files"]["input"] }) =>
          input?.pattern && `Searching files "${input?.pattern}"...`,
        success: ({
          input,
        }: {
          input: ImagineTools["glob_files"]["input"];
          output?: ImagineTools["glob_files"]["output"];
        }) =>
          input?.pattern &&
          `Searched files "${input?.pattern}"${input?.path ? ` in ${input?.path}` : ""}...`,
        error: ({
          input,
        }: {
          input: ImagineTools["glob_files"]["input"];
          output?: ImagineTools["glob_files"]["output"];
        }) =>
          input?.pattern &&
          `Error searching files "${input?.pattern}"${input?.path ? ` in ${input?.path}` : ""}`,
      };

    default:
      return null;
  }
}

function formatPackagesList(pkgs: unknown): string {
  const arr = Array.isArray(pkgs) ? pkgs : [];
  if (arr.length === 0) return "";
  if (arr.length <= 3) return arr.join(", ");
  return `${arr.slice(0, 3).join(", ")} and ${arr.length - 3} more`;
}

export const renderToolCall = (part: ImagineUIToolParts) => {
  if (!part) {
    return undefined;
  }

  const toolCallTextPerState = getToolCallDisplayByState(part);

  const input = (part as any)?.input;

  const output = (part as any)?.output;

  switch (part.state) {
    case "input-streaming":
    case "input-available":
      return toolCallTextPerState?.loading?.({
        input,
      });
    case "output-available":
      return toolCallTextPerState?.success?.({
        input,
        output,
      });
    case "output-error":
      return toolCallTextPerState?.error?.({
        input,
        output,
      });
    default:
      return null;
  }
};
